import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { ActionIcon, Alert, Box, Button, Group, Image, SimpleGrid, Stack, Tabs, Text, Textarea } from "@mantine/core";
import { IconCamera, IconMicrophone, IconPhotoPlus, IconPlayerStopFilled, IconTextCaption, IconUpload, IconVideo, IconX } from "@tabler/icons-react";
import { blobToDataUri, imageToDataUri } from "@passio/api-client";
import { ImagePicker } from "./ImagePicker";
import type { SamplePhoto } from "../lib/samples";
import { MAX_MEDIA_BYTES, MAX_PHOTOS, fmtMB, mediaSizeError, photosMedia, plainMimeType, toWav16k, type MediaKind, type MediaValue } from "../lib/kitchen";

export interface MediaInputValue {
  text: string;
  media: MediaValue | null;
}

type Tab = "text" | MediaKind;

const MAX_VIDEO_SECONDS = 45;
const MAX_VOICE_SECONDS = 60;

/** MediaRecorder around one getUserMedia stream, stopped automatically after `maxSeconds`. */
function useRecorder(maxSeconds: number, onDone: (blob: Blob) => void) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<MediaRecorder | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  }, [onDone]);
  useEffect(
    () => () => {
      window.clearInterval(timer.current);
      rec.current?.stream.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  const stop = () => {
    if (rec.current?.state === "recording") rec.current.stop();
  };
  const start = async (constraints: MediaStreamConstraints, options: MediaRecorderOptions) => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("This browser can't record here. Upload a file instead.");
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      const r = new MediaRecorder(s, options.mimeType && MediaRecorder.isTypeSupported(options.mimeType) ? options : { ...options, mimeType: undefined });
      const chunks: Blob[] = [];
      r.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      r.onstop = () => {
        s.getTracks().forEach((t) => t.stop());
        window.clearInterval(timer.current);
        rec.current = null;
        setStream(null);
        if (chunks.length) done.current(new Blob(chunks, { type: r.mimeType || chunks[0]!.type }));
      };
      r.start(250);
      rec.current = r;
      setStream(s);
      setElapsed(0);
      const began = Date.now();
      timer.current = window.setInterval(() => {
        const sec = (Date.now() - began) / 1000;
        setElapsed(sec);
        if (sec >= maxSeconds) stop();
      }, 200);
    } catch (e) {
      setError(e instanceof Error ? (e.name === "NotAllowedError" ? "Camera or microphone access was blocked." : e.message) : String(e));
    }
  };
  return { recording: Boolean(stream), stream, elapsed, error, start, stop };
}

async function toMedia(kind: MediaKind, blob: Blob, name?: string): Promise<MediaValue> {
  const fallback = kind === "video" ? "video/mp4" : "audio/wav";
  const typed = new Blob([blob], { type: plainMimeType(blob.type, fallback) });
  return { kind, dataUri: await blobToDataUri(typed), bytes: typed.size, name };
}

function MediaPreview({ media, onRemove }: { media: MediaValue; onRemove: () => void }) {
  return (
    <Stack gap={6}>
      <Box pos="relative">
        {media.kind === "video" ? <video src={media.dataUri} controls playsInline style={{ width: "100%", maxHeight: 320, borderRadius: 12, background: "#000" }} /> : <audio src={media.dataUri} controls style={{ width: "100%" }} />}
        {media.kind === "video" && (
          <ActionIcon pos="absolute" top={8} right={8} radius="xl" color="ink.8" onClick={onRemove} aria-label="Remove video">
            <IconX size={16} />
          </ActionIcon>
        )}
      </Box>
      <Group justify="space-between">
        <Text fz="xs" c="dimmed">
          {media.name ? `${media.name} · ` : ""}
          {fmtMB(media.bytes)}
        </Text>
        {media.kind === "audio" && (
          <Button size="compact-xs" variant="subtle" color="gray" onClick={onRemove}>
            Remove
          </Button>
        )}
      </Group>
    </Stack>
  );
}

/** The first photo through the usual picker (samples, paste, drop), then up to MAX_PHOTOS as thumbnails. */
function PhotosPanel({ value, onChange, label, samples }: { value: MediaValue | null; onChange: (m: MediaValue | null) => void; label?: string; samples?: SamplePhoto[] }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const photos = value?.photos ?? (value ? [value.dataUri] : []);
  const add = async (files: File[]) => {
    const picked = files.filter((f) => f.type.startsWith("image/")).slice(0, MAX_PHOTOS - photos.length);
    if (!picked.length) return;
    setBusy(true);
    try {
      onChange(photosMedia([...photos, ...(await Promise.all(picked.map((f) => imageToDataUri(f))))]));
    } finally {
      setBusy(false);
    }
  };
  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.currentTarget.files ?? [])];
    e.currentTarget.value = "";
    void add(files);
  };

  if (!photos.length) {
    return (
      <Stack gap={6}>
        <ImagePicker value={null} onChange={(uri) => onChange(uri ? photosMedia([uri]) : null)} label={label} samples={samples} />
        <Text fz="xs" c="dimmed" ta="center">
          You can add up to {MAX_PHOTOS} photos, e.g. the fridge, the pantry and the counter.
        </Text>
      </Stack>
    );
  }
  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
        {photos.map((p, i) => (
          <Box key={`${i}-${p.length}`} pos="relative">
            <Image src={p} alt={`Photo ${i + 1}`} h={110} radius="md" fit="cover" />
            <ActionIcon pos="absolute" top={4} right={4} size="sm" radius="xl" color="ink.8" onClick={() => onChange(photosMedia(photos.filter((_, j) => j !== i)))} aria-label={`Remove photo ${i + 1}`}>
              <IconX size={12} />
            </ActionIcon>
          </Box>
        ))}
      </SimpleGrid>
      <Group justify="space-between" gap="xs">
        <Text fz="xs" c="dimmed">
          {photos.length} of {MAX_PHOTOS} photos · {fmtMB(value!.bytes)}
        </Text>
        {photos.length < MAX_PHOTOS && (
          <Group gap="xs">
            <Button size="compact-sm" variant="default" leftSection={<IconPhotoPlus size={14} />} loading={busy} onClick={() => fileRef.current?.click()}>
              Add photo
            </Button>
            <Button size="compact-sm" variant="light" color="violet" leftSection={<IconCamera size={14} />} disabled={busy} onClick={() => camRef.current?.click()}>
              Take another
            </Button>
          </Group>
        )}
      </Group>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={onFiles} />
    </Stack>
  );
}

function VideoPanel({ value, onChange }: { value: MediaValue | null; onChange: (m: MediaValue | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const accept = async (blob: Blob | null | undefined, name?: string) => {
    if (!blob) return;
    if (!blob.type.startsWith("video/") && blob.type) return setProblem("That isn't a video file.");
    const tooBig = mediaSizeError(blob.size);
    if (tooBig) return setProblem(tooBig);
    setProblem(null);
    onChange(await toMedia("video", blob, name));
  };
  const rec = useRecorder(MAX_VIDEO_SECONDS, (b) => void accept(b, "recorded clip"));
  if (value) return <MediaPreview media={value} onRemove={() => onChange(null)} />;
  return (
    <Stack gap="xs">
      {rec.recording ? (
        <Stack gap={6}>
          <video
            ref={(el) => {
              if (el && rec.stream && el.srcObject !== rec.stream) el.srcObject = rec.stream;
            }}
            muted
            autoPlay
            playsInline
            style={{ width: "100%", maxHeight: 320, borderRadius: 12, background: "#000" }}
          />
          <Button color="red" leftSection={<IconPlayerStopFilled size={16} />} onClick={rec.stop}>
            Stop ({Math.max(0, Math.ceil(MAX_VIDEO_SECONDS - rec.elapsed))} s left)
          </Button>
        </Stack>
      ) : (
        <Box className="pd-dropzone" p="lg">
          <Stack align="center" gap="sm">
            <IconVideo size={32} color="var(--pd-violet)" />
            <Text fw={600} ta="center">
              Pan slowly across the fridge, pantry or table
            </Text>
            <Text fz="xs" c="dimmed" ta="center">
              Up to {MAX_VIDEO_SECONDS} s recorded here, or upload a clip under {fmtMB(MAX_MEDIA_BYTES)}.
            </Text>
            <Group gap="xs" justify="center">
              <Button variant="default" leftSection={<IconUpload size={16} />} onClick={() => fileRef.current?.click()}>
                Choose video
              </Button>
              <Button variant="light" color="violet" leftSection={<IconCamera size={16} />} onClick={() => void rec.start({ video: { facingMode: "environment", width: { ideal: 1280 } }, audio: false }, { mimeType: "video/mp4", videoBitsPerSecond: 1_200_000 })}>
                Record clip
              </Button>
              <Button variant="subtle" color="violet" onClick={() => camRef.current?.click()} hiddenFrom="sm">
                Phone camera
              </Button>
            </Group>
          </Stack>
        </Box>
      )}
      {(problem || rec.error) && (
        <Text fz="xs" c="red">
          {problem || rec.error}
        </Text>
      )}
      <input ref={fileRef} type="file" accept="video/*" hidden onChange={(e) => void accept(e.currentTarget.files?.[0], e.currentTarget.files?.[0]?.name)} />
      <input ref={camRef} type="file" accept="video/*" capture="environment" hidden onChange={(e) => void accept(e.currentTarget.files?.[0], e.currentTarget.files?.[0]?.name)} />
    </Stack>
  );
}

function VoicePanel({ value, onChange }: { value: MediaValue | null; onChange: (m: MediaValue | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const accept = async (blob: Blob | null | undefined, name?: string) => {
    if (!blob) return;
    setBusy(true);
    setProblem(null);
    try {
      let audio = blob;
      try {
        audio = await toWav16k(blob);
      } catch {
        // Formats the browser can't decode go up as they are; the API reads WAV, MP3, M4A, WebM, OGG and FLAC.
        if (!blob.type.startsWith("audio/")) throw new Error("That isn't an audio file this browser can read.");
      }
      const tooBig = mediaSizeError(audio.size);
      if (tooBig) throw new Error(tooBig);
      onChange(await toMedia("audio", audio, name));
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const rec = useRecorder(MAX_VOICE_SECONDS, (b) => void accept(b, "voice note"));
  if (value) return <MediaPreview media={value} onRemove={() => onChange(null)} />;
  return (
    <Stack gap="xs">
      <Box className="pd-dropzone" p="lg">
        <Stack align="center" gap="sm">
          <IconMicrophone size={32} color={rec.recording ? "var(--mantine-color-red-6)" : "var(--pd-violet)"} />
          <Text fw={600} ta="center">
            {rec.recording ? `Listening… ${Math.floor(rec.elapsed)} s` : "Say what you have and what you feel like"}
          </Text>
          <Text fz="xs" c="dimmed" ta="center">
            Converted to 16 kHz mono WAV in your browser, so any model can read it. Up to {MAX_VOICE_SECONDS} s.
          </Text>
          <Group gap="xs" justify="center">
            {rec.recording ? (
              <Button color="red" leftSection={<IconPlayerStopFilled size={16} />} onClick={rec.stop}>
                Stop
              </Button>
            ) : (
              <Button color="violet" leftSection={<IconMicrophone size={16} />} loading={busy} onClick={() => void rec.start({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } }, {})}>
                Record
              </Button>
            )}
            <Button variant="default" leftSection={<IconUpload size={16} />} disabled={rec.recording || busy} onClick={() => fileRef.current?.click()}>
              Upload audio
            </Button>
          </Group>
        </Stack>
      </Box>
      {(problem || rec.error) && (
        <Text fz="xs" c="red">
          {problem || rec.error}
        </Text>
      )}
      <input ref={fileRef} type="file" accept="audio/*" hidden onChange={(e) => void accept(e.currentTarget.files?.[0], e.currentTarget.files?.[0]?.name)} />
    </Stack>
  );
}

/**
 * Text, Photos, Video and Voice input for the kitchen tools. Up to MAX_PHOTOS photos, or one video or voice note, per call; text goes along with it.
 * Switching tabs drops media of another kind so what's on screen is what gets sent.
 */
export function MediaInput({
  value,
  onChange,
  kinds = ["image", "video", "audio"],
  textPlaceholder,
  photoLabel,
  samples,
}: {
  value: MediaInputValue;
  onChange: (v: MediaInputValue) => void;
  kinds?: MediaKind[];
  textPlaceholder?: string;
  photoLabel?: string;
  samples?: SamplePhoto[];
}) {
  const [tab, setTab] = useState<Tab>(value.media?.kind ?? "text");
  const setMedia = (media: MediaValue | null) => onChange({ ...value, media });
  const switchTab = (t: Tab) => {
    setTab(t);
    if (value.media && value.media.kind !== t) onChange({ ...value, media: null });
  };
  return (
    <Stack gap="sm">
      <Tabs value={tab} onChange={(t) => t && switchTab(t as Tab)} variant="pills" radius="md">
        <Tabs.List grow>
          <Tabs.Tab value="text" leftSection={<IconTextCaption size={15} />}>
            Text
          </Tabs.Tab>
          {kinds.includes("image") && (
            <Tabs.Tab value="image" leftSection={<IconCamera size={15} />}>
              Photos
            </Tabs.Tab>
          )}
          {kinds.includes("video") && (
            <Tabs.Tab value="video" leftSection={<IconVideo size={15} />}>
              Video
            </Tabs.Tab>
          )}
          {kinds.includes("audio") && (
            <Tabs.Tab value="audio" leftSection={<IconMicrophone size={15} />}>
              Voice
            </Tabs.Tab>
          )}
        </Tabs.List>
      </Tabs>
      {tab === "image" && <PhotosPanel value={value.media?.kind === "image" ? value.media : null} onChange={setMedia} label={photoLabel} samples={samples} />}
      {tab === "video" && <VideoPanel value={value.media?.kind === "video" ? value.media : null} onChange={setMedia} />}
      {tab === "audio" && <VoicePanel value={value.media?.kind === "audio" ? value.media : null} onChange={setMedia} />}
      {(tab === "video" || tab === "audio") && (
        <Alert color="violet" variant="light" p="xs">
          <Text fz="xs">Audio and video run on Gemini models only. If you picked another model for this route, the API returns a 400.</Text>
        </Alert>
      )}
      <Textarea
        label={tab === "text" ? undefined : "Anything to add? (optional)"}
        autosize
        minRows={tab === "text" ? 4 : 1}
        maxRows={8}
        placeholder={tab === "text" ? textPlaceholder : "e.g. the chicken needs using up"}
        value={value.text}
        onChange={(e) => onChange({ ...value, text: e.currentTarget.value })}
      />
    </Stack>
  );
}
